import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

interface StickyBannerSettings {
  image_url: string;
  link_url: string | null;
  is_active: boolean;
}

export const StickyBanner = () => {
  const [bannerSettings, setBannerSettings] = useState<StickyBannerSettings | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const { toast } = useToast();
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchBannerSettings = async () => {
      const { data, error } = await supabase
        .from("sticky_banners")
        .select("*")
        .eq('is_active', true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de charger les paramètres de la bannière sticky",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setBannerSettings(data);
      }
    };

    fetchBannerSettings();
  }, [toast]);

  // Hide banner on admin pages
  if (!bannerSettings?.image_url || !bannerSettings.is_active || !isVisible || location.pathname.startsWith('/admin')) {
    return null;
  }

  const BannerContent = () => (
    <div className={`relative mx-auto ${isMobile ? 'w-[300px] h-[50px] mb-4 rounded-lg shadow-lg' : 'w-[728px] h-[90px]'}`}>
      <div
        className="w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${bannerSettings.image_url})`,
        }}
      />
      <button
        onClick={() => setIsVisible(false)}
        className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
        aria-label="Fermer la bannière"
      >
        <X className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center">
      {bannerSettings.link_url ? (
        <a
          href={bannerSettings.link_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block transition-opacity hover:opacity-90"
        >
          <BannerContent />
        </a>
      ) : (
        <BannerContent />
      )}
    </div>
  );
};